// FILE: src/services/copilot.js
import axiosServices from "./axios";

const normalizeCopilotError = (
  error,
  fallbackMessage = "Copilot request failed."
) => {
  const message =
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.message ||
    fallbackMessage;

  const normalizedError = new Error(message);
  normalizedError.statusCode = error.response?.status;
  normalizedError.data = error.response?.data || null;
  normalizedError.code = error.response?.data?.code || null;
  normalizedError.errorType =
    error.response?.data?.errorType || null;
  normalizedError.feature =
    error.response?.data?.feature || null;
  normalizedError.requiredCredits =
    error.response?.data?.requiredCredits ?? null;
  normalizedError.remainingCredits =
    error.response?.data?.remainingCredits ?? null;
  normalizedError.addCreditsPath =
    error.response?.data?.addCreditsPath || null;
  normalizedError.isLimitError =
    normalizedError.errorType === "LIMIT_REACHED";

  return normalizedError;
};

const normalizeAttachmentForApi = (attachment = {}) => {
  const metadata = attachment.metadata || {};
  const file = attachment.file || null;

  return {
    name: attachment.name || file?.name || null,
    type: attachment.type || file?.type || attachment.mimeType || null,
    size: Number(attachment.size || file?.size || 0),
    url: attachment.url || null,
    metadata: {
      ...metadata,
      clientId: attachment.id || metadata.clientId || null,
      clientOnly: !attachment.url,
      uploadedToLibrary: Boolean(metadata.uploadedToLibrary),
      libraryAssetId:
        metadata.libraryAssetId ||
        attachment.libraryAssetId ||
        attachment.assetId ||
        null,
      uploadSource: metadata.uploadSource || null,
      originalName: metadata.originalName || file?.name || attachment.name || null,
    },
  };
};

const normalizeModelForApi = (model = null) => {
  if (!model) return null;

  const value = String(model)
    .trim()
    .toLowerCase()
    .replace(/-/g, " ")
    .replace(/\s+/g, " ");

  if (value === "gpt 5.5") return "gpt 5.5";
  if (value === "grok 4.3") return "grok 4.3";
  if (value === "opus 4.8") return "opus 4.8";

  return model;
};

const normalizeChatResponse = (data = {}) => {
  return {
  ...data,
  attachments: data.attachments || {
  enabled: false,
  count: 0,
  sourceReferences: [],
  },
  knowledge: data.knowledge || {
  enabled: false,
  mode: null,
  count: 0,
  error: null,
  sourceReferences: [],
  },
  usage: data.usage || null,
  pricing: data.pricing || null,
  guardrails: data.guardrails || null,
  guardrailSettings: data.guardrailSettings || data.guardrail_settings || null,
  model: data.model || null,
  llm: data.llm || null,
  };
};

export const createCopilotSession = async ({
  groupId,
  title = "",
  selectedModel = null,
  metadata = {},
}) => {
  try {
    const response = await axiosServices.post("/api/copilot/sessions", {
      groupId,
      title,
      selectedModel: normalizeModelForApi(selectedModel),
      metadata,
    });

    return response.data;
  } catch (error) {
    throw normalizeCopilotError(error, "Failed to create Copilot session.");
  }
};

export const fetchCopilotSessions = async ({
  groupId = null,
  page = 1,
  limit = 20,
} = {}) => {
  try {
    const response = await axiosServices.get("/api/copilot/sessions", {
      params: {
        groupId,
        page,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    throw normalizeCopilotError(error, "Failed to fetch Copilot sessions.");
  }
};

export const fetchCopilotMessages = async (sessionId) => {
  try {
    if (!sessionId) {
      throw new Error("sessionId is required.");
    }

    const response = await axiosServices.get(
      `/api/copilot/sessions/${sessionId}/messages`
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(error, "Failed to fetch Copilot messages.");
    }

    throw error;
  }
};

export const renameCopilotSession = async ({
  sessionId,
  title,
}) => {
  try {
    if (!sessionId) {
      throw new Error("sessionId is required.");
    }

    const response = await axiosServices.patch(
      `/api/copilot/sessions/${sessionId}`,
      {
        title,
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(error, "Failed to rename Copilot session.");
    }

    throw error;
  }
};

export const deleteCopilotSession = async (sessionId) => {
  try {
    if (!sessionId) {
      throw new Error("sessionId is required.");
    }

    const response = await axiosServices.delete(
      `/api/copilot/sessions/${sessionId}`
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(error, "Failed to delete Copilot session.");
    }

    throw error;
  }
};

export const sendCopilotChat = async ({
  groupId,
  sessionId = null,
  message = "",
  attachments = [],
  model = null,
  guardrails = {
    input: true,
    output: true,
  },
  metadata = {},
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    const response = await axiosServices.post("/api/copilot/chat", {
      groupId,
      sessionId,
      message,
      model: normalizeModelForApi(model),
      guardrails,
      metadata,
      attachments: Array.isArray(attachments)
        ? attachments.map(normalizeAttachmentForApi)
        : [],
    });

    return normalizeChatResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(error, "Copilot chat failed.");
    }

    throw error;
  }
};
export const recordCopilotAccountConnectResult =
  async ({
    sessionId,
    resultId,
    platform = null,
    requestedPlatforms = [],
    remainingPlatforms = [],
    accounts = [],
  }) => {
    try {
      if (!sessionId) {
        throw new Error(
          "sessionId is required."
        );
      }

      if (!resultId) {
        throw new Error(
          "resultId is required."
        );
      }

      const response =
        await axiosServices.post(
          `/api/copilot/sessions/${sessionId}/account-connect-result`,
          {
            resultId,
            platform,
            requestedPlatforms:
              Array.isArray(
                requestedPlatforms
              )
                ? requestedPlatforms
                : [],
            remainingPlatforms:
              Array.isArray(
                remainingPlatforms
              )
                ? remainingPlatforms
                : [],
            accounts:
              Array.isArray(
                accounts
              )
                ? accounts
                : [],
          }
        );

      return normalizeChatResponse(
        response.data
      );
    } catch (error) {
      if (error.response) {
        throw normalizeCopilotError(
          error,
          "Failed to record the account connection result."
        );
      }

      throw error;
    }
  };

export const startCopilotReadResultAction = async ({
sessionId,
sourceMessageId,
actionId,
fieldKeys = [],
}) => {
try {
if (!sessionId) {
throw new Error(
"sessionId is required."
);
}

if (!sourceMessageId) {
throw new Error(
"sourceMessageId is required."
);
}

if (!actionId) {
throw new Error(
"actionId is required."
);
}

if (!Array.isArray(fieldKeys)) {
throw new Error(
"fieldKeys must be an array."
);
}

const response =
await axiosServices.post(
`/api/copilot/sessions/${sessionId}/read-result-actions`,
{
sourceMessageId,
actionId,
fieldKeys,
}
);

return normalizeChatResponse(
response.data
);
} catch (error) {
if (error.response) {
throw normalizeCopilotError(
error,
"Failed to start the Brand Kit update."
);
}

throw error;
}
};


export const patchCopilotActionDraft = async ({
  sessionId,
  actionDraftId,
  expectedRevision,
  patch,
  metadata = {},
}) => {
  try {
    if (!sessionId) {
      throw new Error("sessionId is required.");
    }

    if (!actionDraftId) {
      throw new Error("actionDraftId is required.");
    }

    if (
      !Number.isInteger(
        Number(expectedRevision)
      ) ||
      Number(expectedRevision) < 1
    ) {
      throw new Error(
        "expectedRevision must be a positive integer."
      );
    }

    if (
      !patch ||
      typeof patch !== "object" ||
      Array.isArray(patch)
    ) {
      throw new Error(
        "A structured action-draft patch is required."
      );
    }

    const response =
      await axiosServices.patch(
        `/api/copilot/sessions/${sessionId}/action-drafts/${actionDraftId}`,
        {
          expectedRevision:
            Number(expectedRevision),
          patch,
          metadata,
        }
      );

    return normalizeChatResponse(
      response.data
    );
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(
        error,
        "Failed to update the Copilot action."
      );
    }

    throw error;
  }
};

export const cancelCopilotActionDraft = async ({
  sessionId,
  actionDraftId,
  reason = "user_cancelled",
}) => {
  try {
    if (!sessionId) {
      throw new Error("sessionId is required.");
    }

    if (!actionDraftId) {
      throw new Error("actionDraftId is required.");
    }

    const response =
      await axiosServices.post(
        `/api/copilot/sessions/${sessionId}/action-drafts/${actionDraftId}/cancel`,
        {
          reason,
        }
      );

    return normalizeChatResponse(
      response.data
    );
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(
        error,
        "Failed to cancel the Copilot action."
      );
    }

    throw error;
  }
};

export const confirmCopilotAction = async ({
  sessionId,
  actionId,
  confirmationToken,
}) => {
  try {
    if (!sessionId) {
      throw new Error("sessionId is required.");
    }

    if (!actionId) {
      throw new Error("actionId is required.");
    }

    if (!confirmationToken) {
      throw new Error("confirmationToken is required.");
    }

    const response = await axiosServices.post(
      `/api/copilot/sessions/${sessionId}/actions/${actionId}/confirm`,
      {
        confirmationToken,
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(
        error,
        "Failed to confirm the Copilot action."
      );
    }

    throw error;
  }
};

export const editCopilotAction = async ({
sessionId,
actionId,
}) => {
try {
if (!sessionId) {
throw new Error(
"sessionId is required."
);
}

if (!actionId) {
throw new Error(
"actionId is required."
);
}

const response =
await axiosServices.post(
`/api/copilot/sessions/${sessionId}/actions/${actionId}/edit`
);

return normalizeChatResponse(
response.data
);
} catch (error) {
if (error.response) {
throw normalizeCopilotError(
error,
"Failed to reopen the Copilot action for editing."
);
}

throw error;
}
};


export const cancelCopilotAction = async ({
  sessionId,
  actionId,
  reason = "user_cancelled",
}) => {
  try {
    if (!sessionId) {
      throw new Error("sessionId is required.");
    }

    if (!actionId) {
      throw new Error("actionId is required.");
    }

    const response = await axiosServices.post(
      `/api/copilot/sessions/${sessionId}/actions/${actionId}/cancel`,
      {
        reason,
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(
        error,
        "Failed to cancel the Copilot action."
      );
    }

    throw error;
  }
};

export const fetchActiveCopilotGoal = async (
  sessionId
) => {
  try {
    if (!sessionId) {
      throw new Error(
        "sessionId is required."
      );
    }

    const response =
      await axiosServices.get(
        `/api/copilot/sessions/${sessionId}/goals/active`
      );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(
        error,
        "Failed to load the active Copilot goal."
      );
    }

    throw error;
  }
};

export const fetchCopilotGoal = async ({
  sessionId,
  goalId,
}) => {
  try {
    if (!sessionId) {
      throw new Error(
        "sessionId is required."
      );
    }

    if (!goalId) {
      throw new Error(
        "goalId is required."
      );
    }

    const response =
      await axiosServices.get(
        `/api/copilot/sessions/${sessionId}/goals/${goalId}`
      );

    return response.data;
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(
        error,
        "Failed to load the Copilot goal."
      );
    }

    throw error;
  }
};

export const cancelCopilotGoal = async ({
  sessionId,
  goalId,
  reason = "user_cancelled",
}) => {
  try {
    if (!sessionId) {
      throw new Error(
        "sessionId is required."
      );
    }

    if (!goalId) {
      throw new Error(
        "goalId is required."
      );
    }

    const response =
      await axiosServices.post(
        `/api/copilot/sessions/${sessionId}/goals/${goalId}/cancel`,
        {
          reason,
        }
      );

    return normalizeChatResponse(
      response.data
    );
  } catch (error) {
    if (error.response) {
      throw normalizeCopilotError(
        error,
        "Failed to cancel the Copilot goal."
      );
    }

    throw error;
  }
};

const copilotService = {
  createCopilotSession,
  fetchCopilotSessions,
  fetchCopilotMessages,
  renameCopilotSession,
  deleteCopilotSession,
  sendCopilotChat,
recordCopilotAccountConnectResult,
startCopilotReadResultAction,
patchCopilotActionDraft,
  cancelCopilotActionDraft,
confirmCopilotAction,
editCopilotAction,
cancelCopilotAction,
fetchActiveCopilotGoal,
fetchCopilotGoal,
cancelCopilotGoal,
};

export default copilotService;